// v2 lily pad — the real water-lily leaf: a notched pad (a partial-sweep cylinder
// with a wedge cut to its centre) plus a smaller notched companion and a sprout
// pad, carrying a layered water-lily blossom of pointed petals around a golden
// heart. Variants: pink blossom, white blossom, and a closed bud rising from the
// pad. Ported 1:1 from age-of-mcp's v2/lily-pad.ts (the source uses no shared
// helpers — only the local `pad` notch builder, inlined here).
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const TAU = Math.PI * 2;
const NOTCH = 0.62;                                  // wedge cut out of a pad

// A floating pad with the lily leaf's notch: a partial-sweep cylinder whose
// missing wedge faces the azimuth `notchAt`.
function pad(r: number, x: number, z: number, y: number, notchAt: number,
    color: string): Part {
  return {
    geometry: new THREE.CylinderGeometry(r, r * 0.96, 0.02, 8, 1, false,
      notchAt + NOTCH / 2, TAU - NOTCH),
    color, position: [x, y, z],
  };
}

function lilyPadParts(v: number): Part[] {
  const yaw = v * 2.0 + 0.5;
  const parts: Part[] = [
    pad(0.2, 0, 0, 0.012, yaw, "#3f8f4a"),
    pad(0.12, 0.26, 0.11, 0.01, yaw + 2.1, "#4a9c52"),
    pad(0.065, -0.18, -0.17, 0.009, yaw + 4.0, "#57a85c"),
  ];
  if (v % 3 < 2) {
    // the open water-lily blossom on the big pad
    const petal = v % 3 === 0 ? "#f2a8c8" : "#f4efe2";
    const inner = v % 3 === 0 ? "#fbd7e6" : "#fffaf0";
    for (let i = 0; i < 6; i++) {                  // outer whorl, flared wide
      const a = yaw + (i / 6) * TAU;
      parts.push({
        geometry: new THREE.ConeGeometry(0.016, 0.08, 4),
        color: petal,
        position: [Math.cos(a) * 0.034, 0.052, Math.sin(a) * 0.034],
        rotation: [0, -a, -0.55],
      });
    }
    for (let i = 0; i < 4; i++) {                  // inner whorl, upright
      const a = yaw + 0.7 + (i / 4) * TAU;
      parts.push({
        geometry: new THREE.ConeGeometry(0.013, 0.07, 4),
        color: inner,
        position: [Math.cos(a) * 0.016, 0.07, Math.sin(a) * 0.016],
        rotation: [0, -a, -0.22],
      });
    }
    parts.push({ geometry: new THREE.IcosahedronGeometry(0.011, 0),
      color: "#f5c84b", position: [0, 0.085, 0] });
  } else {
    // a closed bud, tipped paler
    parts.push({ geometry: new THREE.ConeGeometry(0.02, 0.06, 6),
      color: "#e89bbd", position: [0, 0.05, 0] });
    parts.push({ geometry: new THREE.ConeGeometry(0.009, 0.025, 6),
      color: "#f2c4d8", position: [0, 0.085, 0] });
  }
  return parts;
}

export const lilyPadV2 = defineModel({
  id: "lily-pad-v2",
  batchId: "fable",
  name: "Lily pad (v2)",
  tags: ["scenery","lily","pad","low-poly","age-of-mcp","fable","water"],
  summary: "Low-poly lily pad.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.34,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => lilyPadParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => lilyPadParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => lilyPadParts(2) },
  ],
  animations: [],
});
